import type {
  ConnectorBindingConfiguration,
  FormField,
  MenuConfiguration,
} from '../../types';
import type { SharedParameterDataType } from '../../types/sharedParameter.types';
import {
  getEquivalentRevitParameterNames,
  normalizeRevitParameterName,
  sanitizeRevitParameterNames,
} from '../revitParameterNames';

export interface ParameterTypeHint {
  name: string;
  dataType: SharedParameterDataType;
}

interface ParameterTypeSource {
  value: string | FormField | undefined;
  dataType: SharedParameterDataType;
}

interface NamedDataType {
  readonly name: string;
  readonly dataType: string;
}

interface SharedParameterTypeDefinition {
  readonly name: string;
  readonly dataType: SharedParameterDataType;
}

interface ConnectorTypeConfiguration {
  menuConfig: MenuConfiguration;
  connectorConfig: ConnectorBindingConfiguration;
}

interface ConnectorFieldContract {
  readValue: (config: ConnectorTypeConfiguration) => string | FormField | undefined;
  dataType: SharedParameterDataType;
}

const CONNECTOR_FIELD_CONTRACTS: readonly ConnectorFieldContract[] = [
  { readValue: ({ menuConfig }) => menuConfig.sKeyValue, dataType: 'TEXT' },
  { readValue: ({ menuConfig }) => menuConfig.objectTypeValue, dataType: 'TEXT' },
  { readValue: ({ menuConfig }) => menuConfig.pipingSpecValue, dataType: 'TEXT' },
  { readValue: ({ menuConfig }) => menuConfig.systemGroupingValue, dataType: 'TEXT' },
  { readValue: ({ connectorConfig }) => connectorConfig.endpoint1Value, dataType: 'TEXT' },
  { readValue: ({ connectorConfig }) => connectorConfig.endpoint2Value, dataType: 'TEXT' },
  { readValue: ({ connectorConfig }) => connectorConfig.endpoint3Value, dataType: 'TEXT' },
  { readValue: ({ connectorConfig }) => connectorConfig.endpoint4Value, dataType: 'TEXT' },
  { readValue: ({ connectorConfig }) => connectorConfig.materialValue, dataType: 'TEXT' },
  { readValue: ({ connectorConfig }) => connectorConfig.designStdValue, dataType: 'TEXT' },
  { readValue: ({ connectorConfig }) => connectorConfig.pressureClassValue, dataType: 'TEXT' },
  { readValue: ({ connectorConfig }) => connectorConfig.insulationSpecValue, dataType: 'TEXT' },
  { readValue: ({ connectorConfig }) => connectorConfig.insulationThicknessValue, dataType: 'TEXT' },
  { readValue: ({ connectorConfig }) => connectorConfig.tracingSpecValue, dataType: 'TEXT' },
  { readValue: ({ connectorConfig }) => connectorConfig.itemDescriptionValue, dataType: 'TEXT' },
];

function readParameterName(value: string | FormField | undefined): string {
  return typeof value === 'string' ? value : value?.value ?? '';
}

function buildParameterTypeHints(sources: readonly ParameterTypeSource[]): ParameterTypeHint[] {
  const hints: ParameterTypeHint[] = [];
  const seenNames = new Set<string>();

  sources.forEach(({ value, dataType }) => {
    const normalized = normalizeRevitParameterName(readParameterName(value).trim());
    const [name] = sanitizeRevitParameterNames([normalized]);
    if (!name) {
      return;
    }

    const lookupName = name.toLowerCase();
    if (seenNames.has(lookupName)) {
      return;
    }

    seenNames.add(lookupName);
    hints.push({ name, dataType });
  });

  return hints;
}

export function buildConnectorParameterTypeHints(
  config: ConnectorTypeConfiguration
): ParameterTypeHint[] {
  return buildParameterTypeHints(
    CONNECTOR_FIELD_CONTRACTS.map(({ readValue, dataType }) => ({
      value: readValue(config),
      dataType,
    }))
  );
}

export function buildTextParameterTypeHints(parameterNames: readonly string[]): ParameterTypeHint[] {
  return buildParameterTypeHints(
    parameterNames.map((value) => ({ value, dataType: 'TEXT' }))
  );
}

export function buildDefinitionBackedParameterTypeHints(
  parameterNames: readonly string[],
  definitions: readonly SharedParameterTypeDefinition[],
  fallbackDataType: SharedParameterDataType = 'TEXT'
): ParameterTypeHint[] {
  const dataTypeByName = new Map<string, SharedParameterDataType>();
  definitions.forEach((definition) => {
    const key = definition.name.trim().toLowerCase();
    // Index actual definitions only so the requested exact name wins before
    // its legacy aliases when equivalent parameters use different data types.
    if (key && !dataTypeByName.has(key)) {
      dataTypeByName.set(key, definition.dataType);
    }
  });

  return buildParameterTypeHints(
    parameterNames.map((value) => {
      const dataType = getEquivalentRevitParameterNames(value)
        .map((name) => dataTypeByName.get(name.trim().toLowerCase()))
        .find((candidate): candidate is SharedParameterDataType => Boolean(candidate));
      return { value, dataType: dataType ?? fallbackDataType };
    })
  );
}

export function applyParameterTypeHints<T extends NamedDataType>(
  definitions: readonly T[],
  hints: readonly NamedDataType[]
): Array<Omit<T, 'dataType'> & NamedDataType> {
  const hintByName = new Map(
    hints.map((hint) => [hint.name.trim().toLowerCase(), hint.dataType])
  );

  return definitions.map((definition) => {
    const hintedType = hintByName.get(definition.name.trim().toLowerCase());
    return hintedType ? { ...definition, dataType: hintedType } : definition;
  });
}
